import type { PersonWrite } from '@pedigreesmart/api-client'
import { corpoPersona } from '@pedigreesmart/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type SyntheticEvent } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './people.css'

type Tipo = 'individual' | 'organization'

/**
 * Il modulo per creare o modificare una persona.
 *
 * **Il tipo comanda il modulo.** Persona fisica e persona giuridica non sono
 * due caselle da spuntare sopra gli stessi campi: cambiano quale nome è
 * obbligatorio, e mostrare «Cognome» accanto a «Ragione sociale» invita a
 * riempirli tutti e due. Qui il tipo è la prima domanda e il resto si adegua.
 *
 * **Gli errori del server finiscono accanto al campo che li ha causati.** Le
 * violazioni arrivano con il percorso in `snake_case` — `last_name`, non
 * `lastName` — che è lo stesso nome dei campi del modulo: l'associazione è
 * diretta, e il messaggio compare dove si guarda invece che in cima alla
 * pagina.
 *
 * **La validazione vera resta sul server.** Qui non si duplica la regola sul
 * nome: sarebbe una seconda copia da tenere allineata, e il gestionale non è
 * l'unico che scrive in anagrafica.
 */
export function PersonForm({
  id,
  onFatto,
  onAnnulla,
}: {
  id?: number | undefined
  onFatto: (id: number) => void
  onAnnulla: () => void
}) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const modifica = typeof id === 'number'

  const esistente = useQuery({
    queryKey: ['person', id],
    queryFn: ({ signal }) => resources.people.get(id as number, signal),
    enabled: modifica,
  })

  const [tipo, setTipo] = useState<Tipo>('individual')
  const [violazioni, setViolazioni] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)

  // In modifica il tipo lo decide il dato finché non lo si tocca: usare lo
  // stato iniziale mostrerebbe i campi della persona fisica a una società.
  const [toccato, setToccato] = useState(false)
  const tipoAttivo: Tipo = toccato ? tipo : (esistente.data?.type ?? tipo)

  const salva = useMutation({
    mutationFn: (corpo: PersonWrite) =>
      modifica ? resources.people.update(id, corpo) : resources.people.create(corpo),
    onMutate: () => {
      setErrore(null)
      setViolazioni({})
    },
    onSuccess: (persona) => {
      void cache.invalidateQueries({ queryKey: ['people'] })
      void cache.invalidateQueries({ queryKey: ['person', persona.id] })
      onFatto(persona.id)
    },
    onError: (e: unknown) => {
      const problema = e as {
        status?: number
        violations?: readonly { propertyPath: string; message: string }[]
      }

      if (problema.status === 422 && problema.violations) {
        setViolazioni(
          Object.fromEntries(problema.violations.map((v) => [v.propertyPath, v.message])),
        )
        setErrore('Alcuni dati non vanno bene: sono segnati qui sotto.')

        return
      }

      setErrore(
        problema.status === 403
          ? 'Il vostro profilo non può modificare l\'anagrafica.'
          : 'Non è stato possibile salvare.',
      )
    },
  })

  if (modifica && esistente.isPending) return <p className="messaggio">Caricamento…</p>

  if (modifica && esistente.isError) {
    return (
      <p role="alert" className="messaggio messaggio--errore">
        Non è stato possibile leggere la scheda da modificare.
      </p>
    )
  }

  const dato = esistente.data

  function invia(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault()

    const campi = new FormData(evento.currentTarget)
    const testo = Object.fromEntries(
      [...campi.entries()].filter(([, v]) => typeof v === 'string'),
    ) as Record<string, string>

    // Le regole su cosa arriva all'API stanno in `core` e hanno le loro
    // prove: qui resta la lettura del modulo, che è l'unica parte che
    // riguarda davvero questa schermata.
    salva.mutate(
      corpoPersona(testo, tipoAttivo, campi.get('breeder') === 'on') as PersonWrite,
    )
  }

  return (
    <section aria-labelledby="titolo-modulo-persona">
      <button type="button" data-variante="piatto" onClick={onAnnulla}>
        ← Annulla
      </button>

      <h1 id="titolo-modulo-persona">
        {modifica ? `Modifica ${dato?.display_name ?? ''}` : 'Nuova persona'}
      </h1>

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      <form className="modulo" onSubmit={invia}>
        <fieldset>
          <legend>Tipo</legend>
          {/* Due pulsanti di scelta e non un menu a tendina: le opzioni sono
              due e cambiano il modulo, quindi conviene vederle entrambe. */}
          {(['individual', 'organization'] as const).map((valore) => (
            <label key={valore} className="modulo__scelta">
              <input
                type="radio"
                name="tipo-persona"
                value={valore}
                checked={tipoAttivo === valore}
                onChange={() => {
                  setToccato(true)
                  setTipo(valore)
                }}
              />
              {valore === 'individual' ? 'Persona fisica' : 'Persona giuridica'}
            </label>
          ))}
        </fieldset>

        <fieldset>
          <legend>Nome</legend>
          {tipoAttivo === 'individual' ? (
            <>
              <Campo
                nome="last_name"
                etichetta="Cognome"
                predefinito={dato?.last_name}
                violazioni={violazioni}
                obbligatorio
              />
              <Campo
                nome="first_name"
                etichetta="Nome"
                predefinito={dato?.first_name}
                violazioni={violazioni}
              />
            </>
          ) : (
            <Campo
              nome="organization_name"
              etichetta="Ragione sociale"
              predefinito={dato?.organization_name}
              violazioni={violazioni}
              obbligatorio
            />
          )}
        </fieldset>

        <fieldset>
          <legend>Allevamento</legend>
          <label className="modulo__interruttore">
            <input type="checkbox" name="breeder" defaultChecked={dato?.breeder ?? false} />
            È un allevatore
          </label>
          <Campo
            nome="kennel_prefix"
            etichetta="Affisso"
            predefinito={dato?.kennel_prefix}
            violazioni={violazioni}
          />
          <Campo
            nome="kennel_prefix_date"
            etichetta="Concesso il"
            tipo="date"
            predefinito={dato?.kennel_prefix_date}
            violazioni={violazioni}
          />
          <Campo
            nome="kennel_suffix"
            etichetta="Suffisso"
            predefinito={dato?.kennel_suffix}
            violazioni={violazioni}
          />
          <Campo
            nome="qualification"
            etichetta="Qualifica"
            predefinito={dato?.qualification}
            violazioni={violazioni}
          />
        </fieldset>

        <fieldset>
          <legend>Contatti</legend>
          <Campo
            nome="email"
            etichetta="Posta elettronica"
            tipo="email"
            predefinito={dato?.email}
            violazioni={violazioni}
          />
          <Campo nome="mobile" etichetta="Cellulare" predefinito={dato?.mobile} violazioni={violazioni} />
          <Campo nome="phone" etichetta="Telefono" predefinito={dato?.phone} violazioni={violazioni} />
        </fieldset>

        <fieldset>
          <legend>Indirizzo</legend>
          <Campo nome="address" etichetta="Via" predefinito={dato?.address} violazioni={violazioni} />
          <Campo nome="postal_code" etichetta="CAP" predefinito={dato?.postal_code} violazioni={violazioni} />
          <Campo nome="city" etichetta="Città" predefinito={dato?.city} violazioni={violazioni} />
          <Campo nome="province" etichetta="Provincia" predefinito={dato?.province} violazioni={violazioni} />
          <Campo
            nome="country"
            etichetta="Paese"
            predefinito={dato?.country ?? 'IT'}
            violazioni={violazioni}
          />
        </fieldset>

        <fieldset>
          <legend>Dati fiscali</legend>
          <Campo nome="tax_code" etichetta="Codice fiscale" predefinito={dato?.tax_code} violazioni={violazioni} />
          <Campo nome="vat_number" etichetta="Partita IVA" predefinito={dato?.vat_number} violazioni={violazioni} />
        </fieldset>

        {/*
          Nascita e documento stanno in fondo e in un riquadro separato: sono i
          dati più delicati della scheda, si compilano di rado, e tenerli fra
          il telefono e la città li farebbe sembrare campi come gli altri.
        */}
        <fieldset>
          <legend>Nascita e documento</legend>
          <Campo
            nome="birth_date"
            etichetta="Data di nascita"
            tipo="date"
            predefinito={dato?.birth_date}
            violazioni={violazioni}
          />
          <Campo nome="birth_place" etichetta="Luogo di nascita" predefinito={dato?.birth_place} violazioni={violazioni} />
          <Campo nome="id_document_type" etichetta="Tipo di documento" predefinito={dato?.id_document_type} violazioni={violazioni} />
          <Campo nome="id_document_number" etichetta="Numero" predefinito={dato?.id_document_number} violazioni={violazioni} />
          <Campo nome="id_document_issuer" etichetta="Rilasciato da" predefinito={dato?.id_document_issuer} violazioni={violazioni} />
          <Campo
            nome="id_document_issued_at"
            etichetta="Rilasciato il"
            tipo="date"
            predefinito={dato?.id_document_issued_at}
            violazioni={violazioni}
          />
          <Campo
            nome="id_document_expires_at"
            etichetta="Scade il"
            tipo="date"
            predefinito={dato?.id_document_expires_at}
            violazioni={violazioni}
          />
        </fieldset>

        <fieldset>
          <legend>Note</legend>
          <label className="modulo__campo modulo__campo--largo">
            <span>Note</span>
            <textarea name="notes" rows={3} defaultValue={dato?.notes ?? ''} />
          </label>
        </fieldset>

        <div className="modulo__azioni">
          <button type="button" data-variante="piatto" onClick={onAnnulla} disabled={salva.isPending}>
            Annulla
          </button>
          <button type="submit" disabled={salva.isPending}>
            {salva.isPending ? 'Salvataggio…' : modifica ? 'Salva le modifiche' : 'Crea la persona'}
          </button>
        </div>
      </form>
    </section>
  )
}

function Campo({
  nome,
  etichetta,
  predefinito,
  violazioni,
  tipo = 'text',
  obbligatorio = false,
}: {
  nome: string
  etichetta: string
  predefinito?: string | null | undefined
  violazioni: Record<string, string>
  tipo?: string
  obbligatorio?: boolean
}) {
  const errore = violazioni[nome]
  const idErrore = `errore-${nome}`

  return (
    <label className="modulo__campo">
      <span>
        {etichetta}
        {obbligatorio && <abbr title="obbligatorio"> *</abbr>}
      </span>
      <input
        type={tipo}
        name={nome}
        defaultValue={tipo === 'date' ? soloData(predefinito) : (predefinito ?? '')}
        aria-invalid={errore ? true : undefined}
        aria-describedby={errore ? idErrore : undefined}
      />
      {/* `role="alert"` e non solo un colore: chi compila con la tastiera e
          uno screen reader deve sapere che il campo è stato rifiutato. */}
      {errore && (
        <span className="modulo__errore" id={idErrore} role="alert">
          {errore}
        </span>
      )}
    </label>
  )
}

/**
 * Le date arrivano come istanti (`1980-01-01T00:00:00+00:00`) e i campi
 * `type="date"` accettano solo `1980-01-01`: senza il taglio il campo resta
 * vuoto e la modifica cancella la data invece di conservarla.
 */
function soloData(iso?: string | null): string {
  return iso ? iso.slice(0, 10) : ''
}
